"use client"

interface RoadmapPhase {
    phase: string
    title: string
    status: "completed" | "in-progress" | "upcoming"
    description: string
    items: string[]
}

const phases: RoadmapPhase[] = [
    {
        phase: "Phase 1",
        title: "Foundation & Launch",
        status: "completed",
        description:
            "Token creation, smart contract deployment, and presale launch across multiple chains.",
        items: [
            "TKS Token deployed on BSC & Ethereum",
            "Multi-chain presale with Chainlink price feeds",
            "Smart contract audits & KYC verification",
            "Token Locker & Vesting system live",
        ],
    },
    {
        phase: "Phase 2",
        title: "Platform Growth",
        status: "in-progress",
        description:
            "Expanding the ecosystem with staking, listing directory, and strategic partnerships.",
        items: [
            "Staking platform launch with multi-tier APY",
            "Token Listing Directory for discovery",
            "Strategic partnerships & exchange listings",
            "Community growth & ambassador program",
        ],
    },
    {
        phase: "Phase 3",
        title: "Strategic Ventures",
        status: "upcoming",
        description:
            "TokenKickstarter will establish two strategic subsidiary ventures designed to generate sustainable revenue. All proceeds are reinvested into the TKS ecosystem to drive long-term token value.",
        items: [
            "Launch first revenue-generating subsidiary venture",
            "Launch second complementary business vertical",
            "All revenue reinvested into the TKS ecosystem",
            "Accelerated marketing & brand awareness campaigns",
        ],
    },
    {
        phase: "Phase 4",
        title: "Ecosystem Expansion",
        status: "upcoming",
        description:
            "A self-sustaining ecosystem where subsidiary revenue, platform fees, and continuous product development drive sustained TKS value appreciation.",
        items: [
            "Product development & new DeFi features",
            "Ecosystem achieves self-sustaining revenue",
            "Marketing investment to drive adoption",
            "TKS value growth through buy-back & utility",
        ],
    },
]

function StatusBadge({ status }: { status: RoadmapPhase["status"] }) {
    const config = {
        completed: {
            label: "Completed",
            dot: "bg-emerald-500",
            classes: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
        },
        "in-progress": {
            label: "In Progress",
            dot: "bg-blue-500",
            classes: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
        },
        upcoming: {
            label: "Upcoming",
            dot: "bg-muted-foreground/40",
            classes: "bg-muted text-muted-foreground",
        },
    }
    const c = config[status]

    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wider ${c.classes}`}>
            {status === "in-progress" ? (
                <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" />
                </span>
            ) : (
                <span className={`h-1.5 w-1.5 rounded-full ${c.dot}`} />
            )}
            {c.label}
        </span>
    )
}

function dotColor(status: RoadmapPhase["status"]) {
    if (status === "completed") return "bg-emerald-500 shadow-emerald-500/30"
    if (status === "in-progress") return "bg-blue-500 shadow-blue-500/30"
    return "bg-muted-foreground/30"
}

function lineColor(status: RoadmapPhase["status"]) {
    if (status === "completed") return "bg-emerald-500/40"
    if (status === "in-progress") return "bg-blue-500/30"
    return "bg-border/40"
}

export function RoadmapSection() {
    return (
        <section className="py-20 md:py-28 relative overflow-hidden" id="roadmap">
            {/* Background */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
            </div>

            <div className="container relative z-10">
                {/* Header */}
                <div className="mb-16 text-center">
                    <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary">
                        Our Vision
                    </div>
                    <h2 className="mb-5 text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl">
                        Project{" "}
                        <span className="bg-gradient-to-r from-primary via-primary to-primary/70 bg-clip-text text-transparent">
                            Roadmap
                        </span>
                    </h2>
                    <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
                        Our strategic roadmap to building a self-sustaining ecosystem — from
                        token launch to revenue-generating sub-companies that fuel TKS growth.
                    </p>
                </div>

                {/* Revenue callout */}
                <div className="glass-card p-6 sm:p-8 mb-16 max-w-3xl mx-auto">
                    <div className="flex items-center gap-3 mb-4">
                        <h3 className="font-bold text-lg">Revenue-Driven Ecosystem</h3>
                        <span className="px-2.5 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-bold">$50B+ Market</span>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                        The TokenKickstarter platform generates revenue directly from its core ecosystem services —
                        the <strong className="text-foreground">Presale Launchpad</strong>,{" "}
                        <strong className="text-foreground">Token Launchpad</strong>, and{" "}
                        <strong className="text-foreground">Staking Platform</strong>. Every presale launched, every token created,
                        and every staking fee contributes revenue back into the TKS ecosystem. In a market with a
                        total valuation of <strong className="text-foreground">$50B+</strong>, TokenKickstarter is positioned
                        to capture significant value.
                    </p>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                        Beyond platform fees, TokenKickstarter will establish <strong className="text-foreground">two strategic subsidiary ventures</strong> designed
                        to generate sustainable, real-world revenue. All proceeds are continuously reinvested into the
                        TKS ecosystem — funding marketing, product development, and buy-back mechanisms that
                        <strong className="text-foreground"> drive long-term TKS token value</strong>. The ecosystem is architected
                        to be self-sustaining and generate revenue independently.
                    </p>
                </div>

                {/* Timeline */}
                <div className="relative max-w-3xl mx-auto">
                    {phases.map((phase, index) => {
                        const isLast = index === phases.length - 1

                        return (
                            <div key={phase.phase} className="relative flex gap-6 sm:gap-8 pb-12 last:pb-0">
                                {/* Left: dot + vertical line */}
                                <div className="flex flex-col items-center shrink-0">
                                    {/* Dot */}
                                    <div className={`w-4 h-4 rounded-full shadow-md ${dotColor(phase.status)} shrink-0 mt-1.5`} />
                                    {/* Connector line */}
                                    {!isLast && (
                                        <div className={`w-0.5 flex-1 mt-2 ${lineColor(phase.status)}`} />
                                    )}
                                </div>

                                {/* Right: content card */}
                                <div className="glass-card p-6 sm:p-7 flex-1 group hover:border-primary/30 transition-all">
                                    <div className="flex flex-wrap items-center gap-3 mb-3">
                                        <span className="text-xs font-bold text-primary uppercase tracking-widest">
                                            {phase.phase}
                                        </span>
                                        <StatusBadge status={phase.status} />
                                    </div>

                                    <h3 className="text-xl font-bold mb-2">{phase.title}</h3>
                                    <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                                        {phase.description}
                                    </p>

                                    <ul className="space-y-2.5">
                                        {phase.items.map((item, i) => (
                                            <li key={i} className="flex items-start gap-3 text-sm">
                                                <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0 mt-1.5" />
                                                <span className="text-muted-foreground group-hover:text-foreground transition-colors">
                                                    {item}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        </section>
    )
}
